import React, { useState } from 'react';
import { MapPin } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { PostcodeChipInput } from '../../components/common/PostcodeChipInput';
import { useToast } from '../../hooks/useToast';
import { PAGE_COLORS } from '../../utils/pageColors';
import {
  useBulkAddDeliveryPostcodesMutation,
  useDeleteDeliveryPostcodeMutation,
  useGetGroupedDeliveryPostcodesQuery,
} from '../../store/slices/apiSlice';
import type { DeliveryPostcodeDayGroup, DeliveryPostcodeRow } from '../../utils/types';
import { normalizePostcodeToken } from '../../utils/postcodeTokens';

const WEEKDAY_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

function formatShortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** Ascending display order (e.g. AL1, BN2, J5, SN16). */
function sortPostcodeRows(rows: DeliveryPostcodeRow[]): DeliveryPostcodeRow[] {
  return [...rows].sort((a, b) =>
    a.postcode.localeCompare(b.postcode, 'en', { numeric: true, sensitivity: 'base' })
  );
}

export const DeliveryPostcodeManagementPage: React.FC = () => {
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useGetGroupedDeliveryPostcodesQuery();
  const [bulkAdd, { isLoading: adding }] = useBulkAddDeliveryPostcodesMutation();
  const [removePc, { isLoading: deleting }] = useDeleteDeliveryPostcodeMutation();

  const [selectedWeekday, setSelectedWeekday] = useState<number>(0);
  const [addChips, setAddChips] = useState<string[]>([]);
  const [editingWeekday, setEditingWeekday] = useState<number | null>(null);
  const [editChips, setEditChips] = useState<string[]>([]);

  const groups = data?.results ?? [];
  const totalCount = groups.reduce((n, g) => n + g.postcodes.length, 0);

  const errDetail = (e: unknown) => {
    const x = e as { data?: { detail?: string } };
    return x?.data?.detail ?? 'Request failed';
  };

  const onAddPostcodes = async () => {
    if (addChips.length === 0) {
      toast.error('No postcodes', 'Add at least one postcode chip before saving.');
      return;
    }
    try {
      const res = await bulkAdd({ weekday: selectedWeekday, postcodes: addChips }).unwrap();
      setAddChips([]);
      const msg =
        res.added_count > 0
          ? `Added ${res.added_count} postcode(s).`
          : 'No new postcodes (all were already present).';
      if (res.skipped_count > 0) {
        toast.success('Postcodes', `${msg} Skipped ${res.skipped_count} duplicate(s).`);
      } else {
        toast.success('Postcodes', msg);
      }
      refetch();
    } catch (e) {
      toast.error('Could not add postcodes', errDetail(e));
    }
  };

  const onDeleteChip = async (row: DeliveryPostcodeRow) => {
    if (!confirm(`Remove postcode ${row.postcode} from ${WEEKDAY_OPTIONS[row.weekday]?.label ?? 'this day'}?`)) {
      return;
    }
    try {
      await removePc(row.id).unwrap();
      toast.success('Removed', `${row.postcode} removed.`);
      refetch();
    } catch (e) {
      toast.error('Delete failed', errDetail(e));
    }
  };

  const startEdit = (group: DeliveryPostcodeDayGroup) => {
    setEditingWeekday(group.weekday);
    setEditChips(sortPostcodeRows(group.postcodes).map((p) => p.postcode));
  };

  const cancelEdit = () => {
    setEditingWeekday(null);
    setEditChips([]);
  };

  const saveEdit = async (group: DeliveryPostcodeDayGroup) => {
    const desired = [...new Set(editChips.map((c) => normalizePostcodeToken(c)).filter(Boolean) as string[])];
    const invalid = editChips.filter((c) => normalizePostcodeToken(c) === null && c.trim() !== '');
    if (invalid.length > 0) {
      toast.error('Invalid postcodes', 'Remove invalid characters; only letters, digits, and spaces are allowed.');
      return;
    }
    const existingByPc = new Map(group.postcodes.map((r) => [r.postcode, r] as const));
    const desiredSet = new Set(desired);

    const toRemove = group.postcodes.filter((r) => !desiredSet.has(r.postcode));
    const toAdd = desired.filter((pc) => !existingByPc.has(pc));

    try {
      for (const r of toRemove) {
        await removePc(r.id).unwrap();
      }
      if (toAdd.length > 0) {
        await bulkAdd({ weekday: group.weekday, postcodes: toAdd }).unwrap();
      }
      toast.success('Updated', `Saved postcodes for ${group.weekday_label}.`);
      cancelEdit();
      refetch();
    } catch (e) {
      toast.error('Update failed', errDetail(e));
    }
  };

  return (
    <AppShell>
      <PageLayout
        title="Delivery Postcode Management"
        subtitle={`${totalCount} postcode(s) across the week`}
        bgColor={PAGE_COLORS.delivery.bgColor}
        textColor={PAGE_COLORS.delivery.textColor}
        borderColor={PAGE_COLORS.delivery.borderColor}
        icon={<MapPin className="w-6 h-6" />}
      >
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Delivery postcode tabs">
            <span className="whitespace-nowrap flex items-center gap-2 border-b-2 border-teal-500 py-4 px-1 text-sm font-medium text-teal-600">
              <MapPin className="h-5 w-5" />
              Manage Postcodes
            </span>
          </nav>
        </div>

        <ContentCard
          title="Add postcodes"
          subtitle="Choose a day, enter one or many postcodes (prefix or full), then add. Existing entries for that day are kept."
        >
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-3">
              <label htmlFor="weekday-select" className="mb-2 block text-sm font-medium text-gray-700">
                Day of week
              </label>
              <select
                id="weekday-select"
                className="form-select w-full rounded-md border-gray-300"
                value={selectedWeekday}
                onChange={(e) => setSelectedWeekday(Number(e.target.value))}
              >
                {WEEKDAY_OPTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="lg:col-span-7">
              <label htmlFor="postcode-chips" className="mb-2 block text-sm font-medium text-gray-700">
                Postcodes
              </label>
              <PostcodeChipInput
                id="postcode-chips"
                chips={addChips}
                onChange={setAddChips}
                disabled={adding}
                aria-label="Postcodes to add"
              />
            </div>
            <div className="lg:col-span-2 flex lg:justify-end">
              <Button type="button" onClick={onAddPostcodes} disabled={adding} className="w-full lg:w-auto">
                {adding ? 'Adding…' : 'Add postcode'}
              </Button>
            </div>
          </div>
        </ContentCard>

        <ContentCard title="Schedule by day" subtitle="Monday through Sunday. Postcodes are listed in ascending order." className="mt-6">
          {isLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          )}
          {isError && !isLoading && (
            <p className="text-sm text-red-600">
              Could not load postcodes. {(error as { data?: { detail?: string } })?.data?.detail ?? 'Unknown error'}
            </p>
          )}
          {!isLoading && !isError && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 w-40">
                      Day
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Postcodes
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500 w-44">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {groups.map((group) => {
                    const isEditing = editingWeekday === group.weekday;
                    return (
                      <tr key={group.weekday}>
                        <td className="px-4 py-3 align-top text-sm font-medium text-gray-900">
                          {group.weekday_label}
                        </td>
                        <td className="px-4 py-3 align-top">
                          {isEditing ? (
                            <PostcodeChipInput
                              chips={editChips}
                              onChange={setEditChips}
                              disabled={deleting || adding}
                              aria-label={`Edit postcodes for ${group.weekday_label}`}
                            />
                          ) : (
                            <div className="pr-1">
                              {group.postcodes.length === 0 ? (
                                <span className="text-sm text-gray-400">No postcodes yet</span>
                              ) : (
                                <div className="flex flex-wrap gap-2">
                                  {sortPostcodeRows(group.postcodes).map((row) => (
                                    <span
                                      key={row.id}
                                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-sm text-gray-800"
                                      title={`Updated ${formatShortDate(row.updated_at)}${
                                        row.updated_by_username ? ` · ${row.updated_by_username}` : ''
                                      }`}
                                    >
                                      {row.postcode}
                                      <button
                                        type="button"
                                        disabled={deleting}
                                        className="rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-50"
                                        onClick={() => onDeleteChip(row)}
                                        aria-label={`Remove ${row.postcode}`}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          {isEditing ? (
                            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                              <Button variant="ghost" size="sm" type="button" onClick={cancelEdit}>
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                type="button"
                                onClick={() => saveEdit(group)}
                                disabled={adding || deleting}
                              >
                                Save
                              </Button>
                            </div>
                          ) : (
                            <Button variant="secondary" size="sm" type="button" onClick={() => startEdit(group)}>
                              Edit
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </ContentCard>
      </PageLayout>
    </AppShell>
  );
};
